import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import FlagImport from 'react-world-flags'
import { Button } from '@/components/shadcn/button'
import { Checkbox } from '@/components/shadcn/checkbox'
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/shadcn/dialog'
import { Input } from '@/components/shadcn/input'
import { ScrollArea } from '@/components/shadcn/scroll-area'
import { useAuthStore } from '@/store/auth.store'
import {Bot, Languages} from 'lucide-react'
import type { PlannedLanguages } from './types'
import {Tooltip, TooltipContent, TooltipTrigger} from "@/components/shadcn/tooltip.tsx";

const Flag = ((FlagImport as unknown as { default?: typeof FlagImport }).default ??
  FlagImport) as typeof FlagImport

const FLAG_CODES: Record<string, string> = {
  en: 'gb', el: 'gr', cs: 'cz', uk: 'ua', he: 'il', zh: 'cn',
  fa: 'ir', sr: 'rs', ar: 'sa', da: 'dk', sv: 'se', et: 'ee', sl: 'si',
}

interface TranslationsDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  title?: string
  value: Record<string, string>
  onApply: (translations: Record<string, string>) => void
  plannedLanguages?: PlannedLanguages
  onPlannedChange?: (planned: PlannedLanguages) => void
  autoTranslateAll?: boolean
}


export function TranslationsDialog({
                                     open,
                                     onOpenChange,
                                     title,
                                     value,
                                     onApply,
                                     plannedLanguages,
                                     onPlannedChange,
                                     autoTranslateAll = false,
                                   }: TranslationsDialogProps) {
  const { t, i18n } = useTranslation('common')
  const supportedLanguages = useAuthStore((s) => s.supportedLanguages)
  const [local, setLocal] = useState<Record<string, string>>(value)
  const [flagError, setFlagError] = useState<Record<string, boolean>>({})

  useEffect(() => {
    if (open) setLocal(value)
  }, [open, value])

  const displayLang = i18n.resolvedLanguage ?? i18n.language
  const languageName = (lang: string) => {
    try {
      return new Intl.DisplayNames([displayLang], { type: 'language' }).of(lang) ?? lang
    } catch {
      return lang
    }
  }

  const handleApply = () => {
    onApply(local)
    onOpenChange(false)
  }

  const showAutoTranslate = plannedLanguages !== undefined
    && onPlannedChange !== undefined
    && !autoTranslateAll

  const autoTranslatableLangs = showAutoTranslate
    ? supportedLanguages.filter((lang) => !local[lang]?.trim())
    : []

  const allPlanned = autoTranslatableLangs.length > 0 &&
    autoTranslatableLangs.every((lang) => plannedLanguages!.has(lang))

  function toggleAll() {
    if (!onPlannedChange) return
    const next = new Set(plannedLanguages)
    if (allPlanned) {
      autoTranslatableLangs.forEach((lang) => next.delete(lang))
    } else {
      autoTranslatableLangs.forEach((lang) => next.add(lang))
    }
    onPlannedChange(next)
  }

  function toggleSingle(lang: string) {
    if (!onPlannedChange) return
    const next = new Set(plannedLanguages)
    if (next.has(lang)) {
      next.delete(lang)
    } else {
      next.add(lang)
    }
    onPlannedChange(next)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-2xl">
        <DialogHeader>
          <DialogTitle>{title ?? t('translations.editTranslations')}</DialogTitle>
        </DialogHeader>

        <ScrollArea className="max-h-[60vh] pr-4">
          <div className="flex flex-col gap-3 py-1">

            {showAutoTranslate && (
              <div className="flex items-center gap-3">
                <div className="w-8 shrink-0" />
                <div className="flex-1" />
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Bot className="h-4 w-4 text-muted-foreground shrink-0 cursor-default" />
                  </TooltipTrigger>
                  <TooltipContent className="max-w-56 text-center">
                    <p className="font-medium">{t('translations.autoTooltipTitle')}</p>
                    <p className="text-xs text-muted-foreground mt-1">{t('translations.autoTooltipDisclaimer')}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            )}

            {supportedLanguages.map((lang) => {
              const hasValue = !!local[lang]?.trim()
              const isPlanned = plannedLanguages?.has(lang) ?? false

              return (
                <div key={lang} className="flex items-center gap-3">
                  {flagError[lang] ? (
                    <span className="w-8 shrink-0 text-center text-xs font-medium text-muted-foreground uppercase">
                      {lang}
                    </span>
                  ) : (
                    <Flag
                      code={FLAG_CODES[lang.toLowerCase()] ?? lang}
                      style={{ width: 32, height: 20 }}
                      className="shrink-0 rounded-sm object-cover"
                      onError={() => setFlagError((prev) => ({ ...prev, [lang]: true }))}
                    />
                  )}
                  <Input
                    placeholder={languageName(lang)}
                    value={local[lang] ?? ''}
                    onChange={(e) => {
                      const newValue = e.target.value
                      setLocal((prev) => ({ ...prev, [lang]: newValue }))
                      if (newValue.trim() && onPlannedChange && plannedLanguages?.has(lang)) {
                        const next = new Set(plannedLanguages)
                        next.delete(lang)
                        onPlannedChange(next)
                      }
                    }}
                  />
                  {showAutoTranslate && !hasValue && (
                    <Checkbox
                      checked={isPlanned}
                      onCheckedChange={() => toggleSingle(lang)}
                      aria-label={languageName(lang)}
                    />
                  )}
                  {showAutoTranslate && hasValue && (
                    <div className="w-4 shrink-0" />
                  )}
                </div>
              )
            })}
          </div>
        </ScrollArea>

        <DialogFooter className="flex-row items-center gap-2">
          {showAutoTranslate && (
            <Button
              type="button"
              variant={allPlanned ? 'default' : 'outline'}
              size="sm"
              onClick={toggleAll}
              disabled={autoTranslatableLangs.length === 0}
            >
              <Languages className="mr-2 h-4 w-4 shrink-0" />
              {allPlanned
                ? t('translations.autoUnplanAll')
                : t('translations.autoSelectAll')
              }
            </Button>
          )}
          <div className="ml-auto flex gap-2">
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              {t('buttons.cancel')}
            </Button>
            <Button type="button" onClick={handleApply}>
              {t('buttons.apply')}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
